import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Truck } from "../../types/truck";
import { getTrucksApi } from "../../api/trucksApi";
import type { RootState } from "../index";

interface AllTrucksState {
  trucks: Truck[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: AllTrucksState = {
  trucks: [],
  loading: false,
  error: null,
  lastFetched: null,
};

const ONE_MINUTE = 60 * 1000; // 60 seconds in milliseconds

export const fetchAllTrucks = createAsyncThunk<
  Truck[],
  void,
  { rejectValue: string; state: RootState }
>("allTrucks/fetchAllTrucks", async (_, { rejectWithValue, getState }) => {
  const state = getState();
  const lastFetched = state.allTrucks.lastFetched;
  const now = Date.now();

  // If we have data and it's less than 1 minute old, return the existing data
  if (lastFetched && now - lastFetched < ONE_MINUTE && state.allTrucks.trucks.length > 0) {
    return state.allTrucks.trucks;
  }

  try {
    return await getTrucksApi();
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch trucks");
  }
});

const allTrucksSlice = createSlice({
  name: "allTrucks",
  initialState,
  reducers: {
    clearAllTrucksError: (state) => {
      state.error = null;
    },
    invalidateTrucksCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllTrucks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTrucks.fulfilled, (state, action: PayloadAction<Truck[]>) => {
        state.loading = false;
        state.trucks = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchAllTrucks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch trucks";
      });
  },
});

export const { clearAllTrucksError, invalidateTrucksCache } = allTrucksSlice.actions;

// Selectors
export const selectAllTrucks = (state: RootState) => state.allTrucks.trucks;
export const selectAllTrucksLoading = (state: RootState) => state.allTrucks.loading;
export const selectAllTrucksError = (state: RootState) => state.allTrucks.error;
export const selectAllTrucksLastFetched = (state: RootState) => state.allTrucks.lastFetched;

export default allTrucksSlice.reducer;
